/**
 * Roadmap — honest "what's shipped vs what's coming"
 *
 * This page exists for a reason: enterprise procurement teams want to see
 * what's REAL and what's marketing. We list every major capability with
 * an honest ship-status flag — Live, In Progress, Roadmap — and real ship
 * targets where committed.
 *
 * No claims appear here that aren't backed by working code (Live) or a
 * committed sprint (In Progress) or a public target date (Roadmap).
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Circle, ArrowRight } from 'lucide-react';

const ITEMS = [
  // ─── LIVE — actually works end-to-end, tested ─────────────────────
  { status: 'live', title: 'Token Auto-Compiler Proxy',
    body: 'Drop-in OpenAI / Anthropic / Gemini / Groq / Mistral proxy with prompt compaction, two-tier cache, and opt-in model cascade. Returns standard responses with X-Cartie-* headers showing real savings.',
    link: { to: '/engine-27', label: 'Try it' } },
  { status: 'live', title: '26 cost-intelligence engines (detect + recommend)',
    body: 'Anomaly detection, idle resource flagging, RI maximizer, multi-cloud spend tracking, carbon-cost optimizer, and 21 more. Live data, real probes against your billing exports.',
    link: { to: '/patent-portfolio', label: 'See all 26 engines' } },
  { status: 'live', title: 'Bill Detective — names the engineer, the commit, the PR',
    body: 'For every cost line in your cloud bill: who introduced it. Engineer leaderboard + Code→Cost drill-down + per-feature + per-customer rollups. Demo accounts get deterministic sample data; real customers populate via GitHub webhook + OTel hooks.',
    link: { to: '/bill-detective', label: 'Open Bill Detective' } },
  { status: 'live', title: 'Automation policies — tiered autonomy + shadow mode',
    body: 'Per-repo tier (T1-T4) + 14-day shadow mode default. Customer controls how aggressively CARTIE may act. Foundation for the PR auto-fixer.',
    link: { to: '/policies/automation', label: 'Configure your repos' } },
  { status: 'live', title: '3× ROI guarantee live tracking',
    body: 'GET /api/me/guarantee-status returns real subscription_paid vs savings_booked math. Dashboard widget tracks status. Stripe live billing ships once Mercury clears.',
    link: { to: '/guarantee', label: 'Read the policy' } },
  { status: 'live', title: 'Audit log — every CARTIE action recorded',
    body: 'Every action you or CARTIE takes is logged with timestamp, actor, target, IP, user-agent. Customer-readable, CSV-exportable, 365-day retention by default.',
    link: { to: '/audit-log', label: 'View your trail' } },
  { status: 'live', title: 'Right-to-Delete (GDPR Article 17)',
    body: 'DELETE /api/me/data wipes everything you own and returns a signed deletion receipt. Tested end-to-end.',
    link: { to: '/security', label: 'Read security policy' } },
  { status: 'live', title: 'API key rotation',
    body: 'POST /api/llm-proxy/keys/:id/rotate atomically issues a new secret and invalidates the old one. Audit-logged.',
    link: { to: '/llm-proxy', label: 'Manage keys' } },
  { status: 'live', title: 'Real-time public status page',
    body: 'Live HTTP probes against MongoDB, AI providers, email + currency vendors. 90-day uptime sparkline computed from real probe data.',
    link: { to: '/status', label: 'Open status' } },
  { status: 'live', title: 'Coordinated vulnerability disclosure (security.txt)',
    body: 'RFC 9116-compliant /.well-known/security.txt. Disclosure contact: Harinathmekala@cartieai.com. 1-day acknowledgment, 5-day initial assessment.',
    link: { to: '/security', label: 'Disclosure policy' } },

  { status: 'live', title: 'Streaming-aware cache',
    body: 'SSE responses are captured chunk-by-chunk on first call and replayed on identical-prompt hits. Unlocks the 80% of production traffic that previous v2 cache bypassed. Same X-Cartie-Cache-Hit header, full streaming UX preserved.',
    link: { to: '/llm-proxy', label: 'See cache stats' } },
  { status: 'live', title: 'How CARTIE works with your team (autonomy explainer)',
    body: 'Public page walking enterprise reviewers through Shadow Mode → T3 → T2 → T1 tiered autonomy with concrete what-happens-when-CARTIE-acts examples per tier.',
    link: { to: '/how-it-works', label: 'See the autonomy tiers' } },
  { status: 'live', title: 'Unified Savings Ledger (cloud engines + Auto-Compiler Proxy)',
    body: 'GET /api/savings/ledger pulls realized AI savings + estimated cloud recommendation savings into a single timeline with per-engine breakdown. Powers the Dashboard banner.',
    link: { to: '/dashboard', label: 'View live numbers' } },

  // ─── IN PROGRESS — committed sprint, code in flight ─────────────────
  { status: 'progress', title: 'GitHub App + PR auto-fixer',
    body: 'Code Tracer is built — now we attach a real GitHub App that opens PRs at the configured tier (respecting shadow mode + excluded paths). Awaiting App registration under the founder\'s account.',
    target: 'Q2 2026' },
  { status: 'progress', title: 'Realized Savings Ledger (cloud-side)',
    body: 'The Auto-Compiler Proxy already books AI savings. Cloud savings get the same treatment — post-merge billing comparison attributing realized $ to specific PRs.',
    target: 'Q2 2026' },
  { status: 'progress', title: 'Slack auto-trigger from spike → trace → PR',
    body: 'When a cost anomaly fires in Slack, CARTIE automatically runs the Code Tracer against the customer repo and either opens a PR (T1/T2/T3) or proposes one (T4).',
    target: 'Q2 2026' },

  // ─── ROADMAP — committed direction, no sprint yet ───────────────────
  { status: 'roadmap', title: 'SOC 2 Type 1 audit',
    body: 'Vanta / Drata readiness work begins once we sign the first enterprise pilot. Audit completion target: 4-6 months from kickoff.',
    target: 'H2 2026' },
  { status: 'roadmap', title: 'pip install cartie-router (1-line SDK)',
    body: 'Optional thin wrapper that auto-injects base_url + X-Cartie-Key. MIT licensed, source on GitHub for full auditability.',
    target: 'Q3 2026' },
  { status: 'roadmap', title: 'Self-hosted deployment option',
    body: 'For air-gapped enterprises. Helm chart + docker-compose, MongoDB or Postgres backend, BYOK cloud bill exports.',
    target: 'H2 2026' },
  { status: 'roadmap', title: 'ISO 27001',
    body: 'After SOC 2 Type 2 (typically Series A timeline).',
    target: '2027' },
];

const STATUS_META = {
  live: {
    icon: CheckCircle2, label: 'LIVE', color: 'emerald',
    bgClass: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300',
    desc: 'Works end-to-end · tested · ship logs in /audit-log',
  },
  progress: {
    icon: Loader2, label: 'IN PROGRESS', color: 'amber',
    bgClass: 'bg-amber-500/10 border-amber-500/40 text-amber-300',
    desc: 'Active sprint · code in flight',
  },
  roadmap: {
    icon: Circle, label: 'ROADMAP', color: 'slate',
    bgClass: 'bg-slate-500/10 border-slate-500/30 text-slate-300',
    desc: 'Committed direction · sprint TBD',
  },
};

export default function Roadmap() {
  const liveCount = ITEMS.filter((i) => i.status === 'live').length;
  const progressCount = ITEMS.filter((i) => i.status === 'progress').length;
  const roadmapCount = ITEMS.filter((i) => i.status === 'roadmap').length;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Helmet>
        <title>Roadmap · What ships when · CARTIE</title>
        <meta name="description" content="Honest map of every CARTIE capability — Live, In Progress, Roadmap — with real ship dates." />
      </Helmet>

      <header className="border-b border-white/5 px-4 py-3 flex items-center justify-between sticky top-0 z-30 bg-slate-950/90 backdrop-blur">
        <Link to="/" className="text-sm font-bold text-emerald-400 hover:text-emerald-300 transition-colors" data-testid="roadmap-logo">
          ← CARTIE
        </Link>
        <div className="flex items-center gap-4 text-xs font-semibold text-slate-400">
          <Link to="/how-it-works" className="hover:text-emerald-300" data-testid="roadmap-nav-how-it-works">How it works</Link>
          <Link to="/guarantee" className="hover:text-emerald-300" data-testid="roadmap-nav-guarantee">3× Guarantee</Link>
          <Link to="/login" className="hover:text-emerald-300" data-testid="roadmap-nav-signin">Sign in</Link>
        </div>
      </header>

      <section className="px-4 py-14 max-w-4xl mx-auto" data-testid="roadmap-hero">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono uppercase tracking-widest mb-5">
          Public · updated weekly
        </div>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]" data-testid="roadmap-h1">
          What&apos;s shipped. What&apos;s coming. <span className="text-emerald-400">No marketing — just facts.</span>
        </h1>
        <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
          Enterprise procurement teams ask &quot;does it actually do that today?&quot;
          before signing anything. So we publish the answer for every capability.
        </p>

        <div className="mt-8 grid sm:grid-cols-3 gap-3" data-testid="roadmap-counters">
          <Counter status="live" count={liveCount} />
          <Counter status="progress" count={progressCount} />
          <Counter status="roadmap" count={roadmapCount} />
        </div>
      </section>

      <section className="px-4 pb-16 max-w-4xl mx-auto space-y-3" data-testid="roadmap-list">
        {ITEMS.map((it, i) => (
          <RoadmapRow key={i} item={it} testid={`roadmap-item-${i}`} />
        ))}
      </section>

      <section className="px-4 py-10 border-t border-white/5 bg-slate-900/40">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-slate-400">
            Want to be a design partner on something in <strong className="text-amber-300">In Progress</strong>?
            We give partners free access + a roadmap vote.
          </p>
          <div className="mt-4">
            <a href="mailto:Harinathmekala@cartieai.com?subject=CARTIE%20Design%20Partner%20program"
               className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm transition-all hover:-translate-y-0.5"
               data-testid="roadmap-partner-cta">
              Become a design partner <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <footer className="px-4 py-6 text-center text-xs text-slate-500 border-t border-white/5">
        <Link to="/" className="text-emerald-400 hover:underline">Home</Link> ·
        {' '}<Link to="/patent-portfolio" className="text-emerald-400 hover:underline">All 26 engines</Link> ·
        {' '}<Link to="/security" className="text-emerald-400 hover:underline">Security</Link> ·
        {' '}<Link to="/status" className="text-emerald-400 hover:underline">Status</Link>
      </footer>
    </div>
  );
}

function Counter({ status, count }) {
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  return (
    <div className={`rounded-xl border p-4 ${meta.bgClass}`} data-testid={`counter-${status}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <span className="text-[10px] font-mono uppercase tracking-widest font-bold">{meta.label}</span>
      </div>
      <div className="text-3xl font-extrabold font-mono tabular-nums mt-1">{count}</div>
      <div className="text-[10px] mt-1 opacity-70 leading-tight">{meta.desc}</div>
    </div>
  );
}

function RoadmapRow({ item, testid }) {
  const meta = STATUS_META[item.status];
  const Icon = meta.icon;
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/40 p-5 hover:border-emerald-500/40 transition-colors" data-testid={testid}>
      <div className="flex items-start gap-4">
        <div className={`shrink-0 inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[10px] font-mono font-bold uppercase tracking-widest ${meta.bgClass}`}>
          <Icon className={`w-3 h-3 ${item.status === 'progress' ? 'animate-spin' : ''}`} />
          {meta.label}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-base font-bold text-white">{item.title}</div>
          <div className="text-sm text-slate-400 mt-1 leading-relaxed">{item.body}</div>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            {item.target && (
              <span className="text-[11px] font-mono text-slate-400">
                Target: <span className="text-amber-300 font-bold">{item.target}</span>
              </span>
            )}
            {item.link && (
              <Link to={item.link.to} className="text-[11px] font-semibold text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1">
                {item.link.label} <ArrowRight className="w-3 h-3" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
